import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import axios from 'axios';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // In production, this would be the API endpoint
        // const response = await axios.get('http://localhost:5000/api/portfolio');
        // setProjects(response.data.data);
        
        // Use mock data for now
        const mockProjects = [
          {
            id: '1',
            title: 'Baringa Projects Management',
            description: 'Collaborated with stakeholders to gather requirements and design scalable solutions for ProjOps CRM. Developed complex solutions to enhance system efficiency and guided the development team.',
            technologies: ['Microsoft Dynamics 365', 'Power Platform', 'JavaScript', 'Azure'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=Baringa+Projects',
            company: 'Delaware India',
            year: '2023-2024',
            category: 'crm'
          },
          {
            id: '2',
            title: 'Costa Franchise Portal',
            description: 'Managed technical design and deployment for UK\'s second largest coffee industry. Handled Powerapps Portal customization and developed Azure functions, Plugins and Power Automate workflows.',
            technologies: ['Powerapps Portal', 'Azure Functions', 'Plugins', 'Power Automate'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=Costa+Franchise+Portal',
            company: 'Delaware India',
            year: '2023',
            category: 'portal'
          },
          {
            id: '3',
            title: 'Calyx Health Domain',
            description: 'Created and managed Azure API Management, deployed APIM with subscription and products, and integrated Ambra with Dynamics CRM. Customized forms, views, reports, and created plugins.',
            technologies: ['Azure API Management', 'Dynamics CRM', 'Power Automate', 'PCF'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=Calyx+Health',
            company: 'NSquare Xperts Pvt. Ltd.',
            year: '2021',
            category: 'healthcare'
          },
          {
            id: '4',
            title: 'Chloe.ai Payment Service',
            description: 'Developed a Dynamics APP for processing payments of won Opportunities. Implemented authentication using Azure and created Azure Functions for API triggers.',
            technologies: ['Dynamics CE', 'Azure Functions', 'App Service', 'JavaScript'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=Chloe.ai',
            company: 'Soho Dragon',
            year: '2019-2020',
            category: 'finance'
          },
          {
            id: '5',
            title: 'PCF Control Library',
            description: 'Developed a set of custom PCF controls for Dynamics 365 to enhance user experience and add additional functionalities not available out-of-the-box.',
            technologies: ['PCF', 'TypeScript', 'React', 'Dynamics 365'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=PCF+Controls',
            company: 'Personal Project',
            year: '2022',
            category: 'development'
          },
          {
            id: '6',
            title: 'Power Platform Administration Tool',
            description: 'Created a custom administration tool for Power Platform environments to help manage multiple instances, users, and data more efficiently.',
            technologies: ['Power Apps', 'Power Automate', 'SharePoint', 'Azure Functions'],
            imageUrl: 'https://via.placeholder.com/800x500.png?text=Admin+Tool',
            company: 'Accenture',
            year: '2022',
            category: 'power-platform'
          }
        ];
        
        setProjects(mockProjects);
        setFilteredProjects(mockProjects);
        setLoading(false);
      } catch (err) {
        setError('Error fetching projects. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  // Filter projects based on category
  const handleFilterChange = (category) => {
    setFilter(category);
    
    if (category === 'all') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.category === category);
      setFilteredProjects(filtered);
    }
  };
  
  return (
    <section className="projects">
      <Container>
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="section-title"
        >
          My Projects
        </motion.h1>
        
        {/* Project Filter */}
        <div className="project-filter">
          <Button 
            variant={filter === 'all' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('all')}
            className="filter-btn"
          >
            All
          </Button>
          <Button 
            variant={filter === 'crm' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('crm')}
            className="filter-btn"
          >
            CRM
          </Button>
          <Button 
            variant={filter === 'power-platform' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('power-platform')}
            className="filter-btn"
          >
            Power Platform
          </Button>
          <Button 
            variant={filter === 'portal' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('portal')}
            className="filter-btn"
          >
            Portals
          </Button>
          <Button 
            variant={filter === 'development' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('development')}
            className="filter-btn"
          >
            Development
          </Button>
          <Button 
            variant={filter === 'healthcare' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('healthcare')}
            className="filter-btn"
          >
            Healthcare
          </Button>
          <Button 
            variant={filter === 'finance' ? 'primary' : 'outline-primary'}
            onClick={() => handleFilterChange('finance')}
            className="filter-btn"
          >
            Finance
          </Button>
        </div>
        
        {/* Projects Grid */}
        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <Row>
            {filteredProjects.map((project, index) => (
              <Col lg={6} key={project.id} className="mb-4">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="project-card"
                >
                  <div className="project-image">
                    <img src={project.imageUrl} alt={project.title} className="img-fluid" />
                    <div className="company-tag">{project.company}</div>
                  </div>
                  <div className="project-content">
                    <div className="project-header">
                      <h3>{project.title}</h3>
                      <span className="project-year">{project.year}</span>
                    </div>
                    <p className="project-description">{project.description}</p>
                    <div className="project-tech">
                      {project.technologies.map((tech, i) => (
                        <span key={i} className="tech-badge">{tech}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        )}
        
        {/* Contact CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="project-cta"
        >
          <h2>Have a project in mind?</h2>
          <p>Let's discuss how I can help you implement or optimize your Microsoft Dynamics 365 solution.</p>
          <Button variant="primary" href="/contact" className="btn-primary">Get in Touch</Button>
        </motion.div>
      </Container>
    </section>
  );
};

export default Projects;